/**
 * Shared helpers for the New Affiliate → Promo codes step. The
 * `Create Promo Code` modal lives in `CreatePromoCodeModal/` and exports
 * its own data file; this one is for what the step itself needs once at
 * least one code exists.
 */

/**
 * Slugify a promo code name to the production URL format — uppercased,
 * with runs of whitespace collapsed to a single `-` (e.g. `"Free 100"`
 * → `"FREE-100"`). Exposed on its own so the summary panel, the deep
 * link, and the `promo_code` we send on save stay in lockstep.
 */
export const buildPromoCodeSlug = (name: string): string =>
  name.trim().replace(/\s+/g, "-").toUpperCase();

// Redirect target AOA points to — the web app reads `referral_code=` to
// attach the promo on web. Built off the app's deep-link host, not the
// AOA host (AOA rejects self-referential targets). Empty when name or
// baseUrl is blank so callers can hide the row.
export const buildAffiliateDeepLinkUrl = (
  name: string,
  baseUrl: string,
): string => {
  const slug = buildPromoCodeSlug(name);
  if (!slug || !baseUrl) return "";
  return `${baseUrl.replace(/\/+$/, "")}/referral_code=${slug}`;
};

/**
 * Hard cap on the promo code `name` length. Enforced by the create modal
 * (antd `max:` rule + `<Input maxLength>`) and consulted by the Duplicate
 * flow in `PromoCodes` to decide whether the auto-numbered name can be
 * committed in one click or whether the operator must pick a fresh name
 * through the modal.
 */
export const PROMO_CODE_NAME_MAX_LENGTH = 16;

/**
 * Trailing `" (N)"` suffix produced by `buildDuplicatePromoCodeName`.
 * Captured here so the regex isn't duplicated between strip + scan.
 */
const DUPLICATE_SUFFIX_PATTERN = /^(.*?)\s+\((\d+)\)$/;

/**
 * Produce a name for a duplicate of `sourceName` that doesn't collide
 * with any entry in `siblings` (the current list, including the source).
 * The `" (N)"` suffix is incremented to the lowest free number for the
 * same root name, so duplicating fills gaps before extending.
 *
 * - `("Free 100", ["Free 100"])` → `"Free 100 (1)"`
 * - `("Free 100", ["Free 100", "Free 100 (1)"])` → `"Free 100 (2)"`
 * - `("Free 100", ["Free 100", "Free 100 (2)"])` → `"Free 100 (1)"`
 * - `("Free 100 (1)", ["Free 100 (1)"])` → `"Free 100 (2)"`
 *
 * The source's own root is derived by stripping any trailing `" (N)"`
 * suffix, so duplicating a copy still numbers against the original.
 *
 * Callers must enforce any external length cap themselves: this helper
 * always returns the shortest available copy, but a long source root
 * can push the result over `PROMO_CODE_NAME_MAX_LENGTH`. `PromoCodes`
 * length-checks the return value and falls back to the create modal
 * when the auto-numbered name would not fit.
 */
export const buildDuplicatePromoCodeName = (
  sourceName: string,
  siblings: ReadonlyArray<string>,
): string => {
  const rootName = sourceName.replace(/\s+\(\d+\)$/, "");
  const usedNumbers = new Set<number>();

  for (const name of siblings) {
    const match = name.match(DUPLICATE_SUFFIX_PATTERN);
    if (match && match[1] === rootName) {
      usedNumbers.add(Number(match[2]));
    }
  }

  let next = 1;
  while (usedNumbers.has(next)) {
    next += 1;
  }

  return `${rootName} (${next})`;
};
